/**
 * Client documents — at present the signed Master Services Agreement captured during
 * onboarding.
 *
 * Reading is available to a ClientAdmin for their own organization and to a SystemAdmin for
 * any. Uploading, replacing and removing are SystemAdmin only. Those rules are enforced by the
 * API against the caller's own claims; the checks in the UI only decide what to draw.
 */

import { makeAuthenticatedRequest, getErrorMessage } from './apiService';
import { API_ENDPOINTS } from '../config/api';

export interface ClientDocument {
  id: string;
  documentType: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByName: string | null;
  uploadedAt: string;
}

/** The client's current documents. Metadata only — the file comes from downloadDocument. */
export const listDocuments = async (clientId: string): Promise<ClientDocument[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENT_DOCUMENTS.LIST(clientId), {
    method: 'GET',
  });

  // A reader without permission gets 403. That is an ordinary outcome for a page that asks
  // before it knows, so report it as "nothing to show" rather than an error.
  if (response.status === 403) return [];
  if (!response.ok) throw new Error('Could not load documents.');

  return response.json();
};

/** Uploads the signed MSA, replacing whatever is on file. SystemAdmin only. */
export const uploadMsa = async (
  clientId: string,
  file: File,
  note?: string
): Promise<ClientDocument> => {
  const form = new FormData();
  form.append('file', file);
  if (note) form.append('note', note);

  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENT_DOCUMENTS.UPLOAD_MSA(clientId), {
    method: 'POST',
    body: form,
  });

  if (!response.ok) {
    // The API explains refusals precisely — wrong file type, too large — and that wording is
    // more useful to the person at the keyboard than anything invented here.
    const problem = await response.json().catch(() => null);
    throw new Error(problem?.detail || problem?.title || 'The upload failed.');
  }

  return response.json();
};

/** Downloads a document and hands it to the browser. */
export const downloadDocument = async (
  clientId: string,
  documentId: string,
  fileName: string
): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.CLIENT_DOCUMENTS.DOWNLOAD(clientId, documentId),
    { method: 'GET' }
  );

  if (!response.ok) throw new Error('Could not download the document.');

  // The endpoint needs an Authorization header, so the file cannot simply be linked. Fetch it,
  // hand the browser a temporary object URL, then release it — without the revoke the blob is
  // held for the life of the page.
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

/** Removes a document from view. SystemAdmin only. The stored copy is kept, not destroyed. */
export const removeDocument = async (
  clientId: string,
  documentId: string,
  reason?: string
): Promise<void> => {
  const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  const response = await makeAuthenticatedRequest(
    `${API_ENDPOINTS.CLIENT_DOCUMENTS.REMOVE(clientId, documentId)}${query}`,
    { method: 'DELETE' }
  );

  if (!response.ok) throw new Error('Could not remove the document.');
};

/** "2.4 MB" — sizes are shown to people, not summed. */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export { getErrorMessage };
